import HeadingPreviousNext from '~/components/forms/questions/scheduling/HeadingPreviousNext';
import {
    endOfMonth,
    format,
    startOfMonth,
    addDays,
    isSameDay,
    isSameMonth,
    isPast,
    endOfDay,
    isToday,
} from 'date-fns';
import useWeekStart from '~/lib/useWeekStart.js';
import SingleClassBlock from '~/components/forms/questions/scheduling/SingleClassBlock.jsx';
import DayHeading from '~/components/forms/questions/scheduling/DayHeading.jsx';
import { isInstanceSelected } from '~/components/forms/questions/scheduling/ClassDescription.jsx';
import ClassViewModal from '~/components/forms/questions/scheduling/ClassViewModal.jsx';
import { useState } from 'react';
import { FormPart, FormStateName, part, state } from '~/lib/FormCssApi';

const MonthlyClassList = ({
    start,
    end,
    settings,
    selectedClasses,
    onSelectClass,
    onRemoveClass,
    classes,
    noAvailabilityComponent,
    clientTz,
}) => {
    const [viewingClass, setViewingClass] = useState(null);
    const weekStart = useWeekStart();
    const month = startOfMonth(start);
    const monthlyCalendar = [];
    const monthlyViewStart = new Date(month);
    monthlyViewStart.setDate(1 - monthlyViewStart.getDay() + weekStart);
    if (monthlyViewStart > month) {
        monthlyViewStart.setDate(monthlyViewStart.getDate() - 7);
    }

    // Create calendar grid (6 weeks to ensure we cover the month)
    for (let week = 0; week < 6; week++) {
        let weekdays = [];
        for (let dow = 0; dow < 7; dow++) {
            weekdays.push(addDays(monthlyViewStart, week * 7 + dow));
        }

        if (
            isSameMonth(weekdays[0], month) ||
            isSameMonth(weekdays[6], month)
        ) {
            monthlyCalendar.push(weekdays);
        }
    }

    // Filter classes for a specific date
    const getClassesForDate = date => {
        return classes.filter(classItem =>
            isSameDay(new Date(classItem.start), date),
        );
    };

    const hasNoAvailability = classes.length === 0;

    return (
        <div className="relative">
            {hasNoAvailability && noAvailabilityComponent}

            <div className="grid grid-cols-7 gap-2 mb-2">
                {monthlyCalendar[0].map((day, index) => (
                    <div
                        key={index}
                        {...part(FormPart.CLASS_CALENDAR_WEEKDAY)}
                        className="text-center text-gray-500 text-sm"
                    >
                        {format(day, 'EEE')}
                    </div>
                ))}
            </div>

            <div
                {...part(FormPart.CLASS_LIST)}
                className="grid grid-cols-7 gap-2"
            >
                {monthlyCalendar.map((week, weekIndex) =>
                    week.map((day, dayIndex) => (
                        <div
                            key={`${weekIndex}-${dayIndex}`}
                            {...part(FormPart.CLASS_CALENDAR_DAY)}
                            {...state(
                                isToday(day) && FormStateName.TODAY,
                                (!isSameMonth(day, month) ||
                                    isPast(endOfDay(day))) &&
                                    FormStateName.UNAVAILABLE,
                            )}
                            className={`min-h-[100px] bg-[#FFFFFF66] rounded-lg p-2 ${isToday(day) ? 'themed-text font-semibold' : ''} ${!isSameMonth(day, month) || isPast(endOfDay(day)) ? 'opacity-50' : ''}`}
                        >
                            <div
                                {...part(FormPart.CLASS_CALENDAR_DAY_NUMBER)}
                                className="text-right text-xs mb-1"
                            >
                                {format(day, 'd')}
                            </div>
                            <div className="space-y-1">
                                {getClassesForDate(day).map(classItem => (
                                    <SingleClassBlock
                                        key={classItem.id}
                                        instance={classItem}
                                        onClick={() =>
                                            setViewingClass(classItem)
                                        }
                                        settings={settings}
                                        selectedClasses={selectedClasses}
                                        clientTz={clientTz}
                                    />
                                ))}
                            </div>
                        </div>
                    )),
                )}
            </div>
            <ClassViewModal
                isOpen={!!viewingClass}
                instance={viewingClass}
                onClose={() => setViewingClass(null)}
                onSelect={onSelectClass}
                onRemoveClass={onRemoveClass}
                selectedClasses={selectedClasses}
                settings={settings}
                clientTz={clientTz}
            />
        </div>
    );
};

const ClassMonthlyView = ({
    start,
    end,
    onPrevious,
    onNext,
    settings,
    selectedClasses,
    onSelectClass,
    onRemoveClass,
    classes,
    noAvailabilityComponent,
    clientTz,
}) => {
    end = endOfMonth(start);

    return (
        <div className="w-[1000px]">
            <HeadingPreviousNext
                className="mb-2"
                onPrevious={onPrevious}
                onNext={onNext}
                start={start}
                end={end}
            >
                {format(start, 'LLLL yyyy')}
            </HeadingPreviousNext>
            <MonthlyClassList
                start={start}
                end={end}
                settings={settings}
                selectedClasses={selectedClasses}
                onSelectClass={onSelectClass}
                onRemoveClass={onRemoveClass}
                classes={classes}
                noAvailabilityComponent={noAvailabilityComponent}
                clientTz={clientTz}
            />
        </div>
    );
};

export default ClassMonthlyView;
